/**
 * PullToRefresh Component
 *
 * A touch-based pull-to-refresh component for mobile web.
 * Provides a native-like pull-to-refresh experience with visual indicators.
 *
 * Features:
 * - Touch gesture detection (pull down to refresh)
 * - Visual loading indicator with spinner
 * - Customizable threshold and resistance
 * - Callback for triggering data refetch
 * - Works within scrollable containers
 *
 * Usage:
 * ```tsx
 * <PullToRefresh onRefresh={handleRefresh} isRefreshing={isLoading}>
 *   <YourContent />
 * </PullToRefresh>
 * ```
 */

import * as React from "react";
import { cn } from "@/lib/utils";
import { RefreshCw, ArrowDown } from "lucide-react";

// ============================================================================
// Types
// ============================================================================

export interface PullToRefreshProps {
  /** Children content to render inside the scrollable area */
  children: React.ReactNode;
  /** Callback when refresh is triggered */
  onRefresh: () => Promise<void> | void;
  /** Whether data is currently being refreshed */
  isRefreshing?: boolean;
  /** Minimum pull distance required to trigger refresh (default: 80px) */
  threshold?: number;
  /** Resistance factor for pull gesture (higher = more resistance, default: 2.5) */
  resistance?: number;
  /** Custom class name for the container */
  className?: string;
  /** Whether pull-to-refresh is disabled */
  disabled?: boolean;
}

interface PullState {
  /** Current pull distance */
  pullDistance: number;
  /** Whether user is actively pulling */
  isPulling: boolean;
  /** Whether the refresh threshold has been reached */
  thresholdReached: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_THRESHOLD = 80;
const DEFAULT_RESISTANCE = 2.5;
const MAX_PULL_DISTANCE = 150;
const INDICATOR_HEIGHT = 50;

// ============================================================================
// Component
// ============================================================================

export function PullToRefresh({
  children,
  onRefresh,
  isRefreshing = false,
  threshold = DEFAULT_THRESHOLD,
  resistance = DEFAULT_RESISTANCE,
  className,
  disabled = false,
}: PullToRefreshProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const startYRef = React.useRef<number>(0);
  const [pullState, setPullState] = React.useState<PullState>({
    pullDistance: 0,
    isPulling: false,
    thresholdReached: false,
  });

  // Handle touch start
  const handleTouchStart = React.useCallback(
    (e: React.TouchEvent) => {
      if (disabled || isRefreshing) return;

      const container = containerRef.current;
      if (!container) return;

      // Only start pulling if we're at the top of the scrollable area
      if (container.scrollTop <= 0) {
        const touch = e.touches[0];
        if (touch) {
          startYRef.current = touch.clientY;
          setPullState((prev) => ({ ...prev, isPulling: true }));
        }
      }
    },
    [disabled, isRefreshing]
  );

  // Handle touch move
  const handleTouchMove = React.useCallback(
    (e: React.TouchEvent) => {
      if (disabled || isRefreshing || !pullState.isPulling) return;

      const container = containerRef.current;
      if (!container) return;

      // Only continue if we're at the top
      if (container.scrollTop > 0) {
        setPullState({
          pullDistance: 0,
          isPulling: false,
          thresholdReached: false,
        });
        return;
      }

      const touch = e.touches[0];
      if (!touch) return;

      const pullDelta = touch.clientY - startYRef.current;

      // Only handle downward pulls
      if (pullDelta > 0) {
        // Prevent default scrolling behavior
        e.preventDefault();

        // Apply resistance to the pull
        const adjustedPull = Math.min(
          pullDelta / resistance,
          MAX_PULL_DISTANCE
        );

        setPullState({
          pullDistance: adjustedPull,
          isPulling: true,
          thresholdReached: adjustedPull >= threshold,
        });
      }
    },
    [disabled, isRefreshing, pullState.isPulling, resistance, threshold]
  );

  // Handle touch end
  const handleTouchEnd = React.useCallback(async () => {
    if (disabled || isRefreshing) return;

    const { thresholdReached } = pullState;

    // Reset pull state immediately
    setPullState({
      pullDistance: 0,
      isPulling: false,
      thresholdReached: false,
    });

    // Trigger refresh if threshold was reached
    if (thresholdReached) {
      try {
        await onRefresh();
      } catch (error) {
        // Error handling is done by the parent component
        console.error("Pull to refresh error:", error);
      }
    }
  }, [disabled, isRefreshing, pullState, onRefresh]);

  // Calculate indicator styles based on pull distance
  const indicatorOpacity = Math.min(pullState.pullDistance / threshold, 1);
  const indicatorScale = 0.5 + (indicatorOpacity * 0.5);
  const rotation = (pullState.pullDistance / threshold) * 180;

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative h-full overflow-y-auto overscroll-y-contain",
        className
      )}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
    >
      {/* Pull indicator */}
      <div
        className={cn(
          "absolute left-0 right-0 z-50 flex items-center justify-center transition-all duration-200",
          "pointer-events-none"
        )}
        style={{
          top: -INDICATOR_HEIGHT,
          height: INDICATOR_HEIGHT,
          transform: `translateY(${pullState.pullDistance}px)`,
          opacity: indicatorOpacity,
        }}
      >
        <div
          className={cn(
            "flex items-center justify-center rounded-full p-2",
            "bg-background/95 shadow-md border border-border/50 backdrop-blur-sm",
            isRefreshing && "animate-pulse"
          )}
          style={{
            transform: `scale(${indicatorScale})`,
          }}
        >
          {isRefreshing ? (
            <RefreshCw className="h-5 w-5 text-primary animate-spin" />
          ) : pullState.thresholdReached ? (
            <RefreshCw
              className="h-5 w-5 text-primary"
              style={{ transform: `rotate(${rotation}deg)` }}
            />
          ) : (
            <ArrowDown
              className="h-5 w-5 text-muted-foreground"
              style={{ transform: `rotate(${rotation}deg)` }}
            />
          )}
        </div>
      </div>

      {/* Pull hint text */}
      {(pullState.isPulling || isRefreshing) && (
        <div
          className={cn(
            "absolute left-0 right-0 z-40 flex items-center justify-center",
            "text-xs font-medium transition-opacity duration-200",
            "pointer-events-none"
          )}
          style={{
            top: pullState.pullDistance + 4,
            opacity: Math.min(indicatorOpacity * 0.8, 0.8),
          }}
        >
          <span
            className={cn(
              "px-2 py-1 rounded-full bg-background/80 backdrop-blur-sm",
              pullState.thresholdReached
                ? "text-primary"
                : "text-muted-foreground"
            )}
          >
            {isRefreshing
              ? "Refreshing..."
              : pullState.thresholdReached
                ? "Release to refresh"
                : "Pull to refresh"}
          </span>
        </div>
      )}

      {/* Content with transform for pull effect */}
      <div
        className="transition-transform duration-200 ease-out"
        style={{
          transform: `translateY(${pullState.isPulling ? pullState.pullDistance : 0}px)`,
        }}
      >
        {children}
      </div>
    </div>
  );
}

// ============================================================================
// Export
// ============================================================================

export default PullToRefresh;
